// packages/report-components/src/backdropGeometry.ts
//
// The drawing of every backdrop shape, as SVG path data on a `0 0 100 100`
// viewBox (plan 2026-10-09-dashboard-shapes-arrows-and-callouts, D3).
//
// ONE module for both surfaces: the dashboard and the report page draw the same
// star, and a star drawn twice is a star that drifts. Pure data plus the small
// helpers that build it; nothing here knows about colour, pixels or React.
//
// Each table is typed `Record<Member, …>` over the closed sets in
// `drawingTokens.ts`, so a member added there without a drawing here fails
// `tsc` rather than drawing nothing; a coverage test iterates the EMITTED sets
// as well, so a member added on the backend alone is caught too.
//
// Geometry touches the viewBox edges on purpose: the `<svg>` draws with
// `overflow: visible`, so a non-scaling outline on an edge is not shaved in
// half, and a figure fills exactly the box the author dragged.

import type { ArrowDirection, ArrowStyle, BackdropFigure, CalloutTail } from "./drawingTokens";

/** One path of a shape. `filled: false` is an outline-only stroke (an arrow's
 *  line, a cylinder's front rim): it must never take the shape's fill, or a
 *  line arrow would draw a filled wedge. */
export type BackdropPath = { d: string; filled: boolean };

type Point = readonly [number, number];

const n = (v: number): string => String(Math.round(v * 100) / 100);

/** A closed polygon, or an open polyline when `closed` is false. */
function poly(points: readonly Point[], closed = true): string {
  const [first, ...rest] = points;
  const body = `M${n(first[0])} ${n(first[1])}` + rest.map(([x, y]) => ` L${n(x)} ${n(y)}`).join("");
  return closed ? `${body} Z` : body;
}

/** A regular star or polygon, scaled so its bounding box is exactly 0..100 on
 *  both axes: a pentagon's natural box is not square, and a figure that does
 *  not reach its own box would sit off-centre in the frame the author drew. */
function fitted(points: readonly Point[]): Point[] {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return points.map(([x, y]) => [((x - x0) / (x1 - x0)) * 100, ((y - y0) / (y1 - y0)) * 100]);
}

function regular(corners: number, innerRatio: number | null): Point[] {
  const out: Point[] = [];
  const steps = innerRatio === null ? corners : corners * 2;
  for (let i = 0; i < steps; i += 1) {
    const r = innerRatio !== null && i % 2 === 1 ? innerRatio : 1;
    // Start straight up, so a pentagon and a star stand on their base.
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / steps;
    out.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return fitted(out);
}

/** The corner of a stretched callout, in px. */
const CORNER_PX = 12;

export const FIGURE_GEOMETRY: Readonly<Record<BackdropFigure, readonly BackdropPath[]>> = {
  rectangle: [{ d: "M0 0 H100 V100 H0 Z", filled: true }],
  circle: [{ d: "M50 0 A50 50 0 1 1 50 100 A50 50 0 1 1 50 0 Z", filled: true }],
  triangle: [{ d: poly([[50, 0], [100, 100], [0, 100]]), filled: true }],
  heart: [
    {
      d:
        "M50 100 C20 76 0 56 0 30 C0 12 13 0 28 0 C40 0 47 8 50 16 " +
        "C53 8 60 0 72 0 C87 0 100 12 100 30 C100 56 80 76 50 100 Z",
      filled: true,
    },
  ],
  diamond: [{ d: poly([[50, 0], [100, 50], [50, 100], [0, 50]]), filled: true }],
  pentagon: [{ d: poly(regular(5, null)), filled: true }],
  // The classic five-pointed star: inner radius ≈ 0.382 of the outer one.
  star: [{ d: poly(regular(5, 0.382)), filled: true }],
  hexagon: [{ d: poly([[25, 0], [75, 0], [100, 50], [75, 100], [25, 100], [0, 50]]), filled: true }],
  octagon: [
    {
      d: poly([[30, 0], [70, 0], [100, 30], [100, 70], [70, 100], [30, 100], [0, 70], [0, 30]]),
      filled: true,
    },
  ],
  // A body whose top is an ellipse seen from above: the outline (top arc over
  // the back, sides, bottom arc), then the front half of the top ellipse as a
  // stroke of its own — the line that makes it read as a drum and not a pill.
  cylinder: [
    { d: "M0 12 A50 12 0 0 1 100 12 V88 A50 12 0 0 1 0 88 Z", filled: true },
    { d: "M0 12 A50 12 0 0 0 100 12", filled: false },
  ],
};

// ── Arrows ──────────────────────────────────────────────────────────────────
// Drawn once pointing right and turned for the other three directions, so the
// four can never disagree about the head's proportions.

const LINE_ARROW_RIGHT: { points: Point[]; closed: boolean }[] = [
  { points: [[0, 50], [100, 50]], closed: false },
  { points: [[78, 28], [100, 50], [78, 72]], closed: false },
];
const BLOCK_ARROW_RIGHT: Point[] = [
  [0, 32], [60, 32], [60, 0], [100, 50], [60, 100], [60, 68], [0, 68],
];

const TURN: Readonly<Record<ArrowDirection, (p: Point) => Point>> = {
  right: ([x, y]) => [x, y],
  left: ([x, y]) => [100 - x, y],
  // The tip (100, 50) lands on (50, 100) and the tail (0, 50) on (50, 0).
  down: ([x, y]) => [100 - y, x],
  up: ([x, y]) => [y, 100 - x],
};

function arrow(direction: ArrowDirection): Record<ArrowStyle, readonly BackdropPath[]> {
  const turn = TURN[direction];
  return {
    line: LINE_ARROW_RIGHT.map(({ points, closed }) => ({
      d: poly(points.map(turn), closed),
      filled: false,
    })),
    block: [{ d: poly(BLOCK_ARROW_RIGHT.map(turn)), filled: true }],
  };
}

export const ARROW_GEOMETRY: Readonly<Record<ArrowDirection, Readonly<Record<ArrowStyle, readonly BackdropPath[]>>>> = {
  right: arrow("right"),
  left: arrow("left"),
  up: arrow("up"),
  down: arrow("down"),
};

// ── Callouts ────────────────────────────────────────────────────────────────
// A rounded body plus a tail, as ONE closed path, so the outline runs round
// the tail without a seam where the two meet. `body` is the box the text may
// use, in viewBox units (= percent of the shape's box): the text stays out of
// the tail.

export type CalloutBodyBox = { left: number; top: number; right: number; bottom: number };
export type CalloutGeometry = { paths: readonly BackdropPath[]; body: CalloutBodyBox };

/** Where the tail cuts into the body: which edge, and the notch's three points
 *  in the direction the outline travels (clockwise from the top-left). */
type Tail = { edge: "top" | "right" | "bottom" | "left"; notch: Point[] };

const TAIL_DEPTH = 18;
const CALLOUT_RADIUS = 6;

const TAILS: Readonly<Record<CalloutTail, Tail | null>> = {
  top_left: { edge: "top", notch: [[14, TAIL_DEPTH], [8, 0], [30, TAIL_DEPTH]] },
  top_right: { edge: "top", notch: [[70, TAIL_DEPTH], [92, 0], [86, TAIL_DEPTH]] },
  right: { edge: "right", notch: [[100 - TAIL_DEPTH, 38], [100, 50], [100 - TAIL_DEPTH, 62]] },
  bottom_right: { edge: "bottom", notch: [[86, 100 - TAIL_DEPTH], [92, 100], [70, 100 - TAIL_DEPTH]] },
  bottom_left: { edge: "bottom", notch: [[30, 100 - TAIL_DEPTH], [8, 100], [14, 100 - TAIL_DEPTH]] },
  left: { edge: "left", notch: [[TAIL_DEPTH, 62], [0, 50], [TAIL_DEPTH, 38]] },
  none: null,
};

/** The corner arcs' radii in viewBox units. On a STRETCHED shape
 *  (`preserveAspectRatio="none"`) one viewBox unit is `width/100` px across
 *  and `height/100` px down, so a radius given in viewBox units comes out as a
 *  flat ellipse on a wide panel. Given the box in px, this returns the
 *  elliptical radii that draw a true `px`-radius quarter circle; without a box
 *  (a proportional shape, scaled uniformly) it is the plain radius. */
function cornerRadii(radius: number, box?: { width: number; height: number }): [number, number] {
  if (!box || box.width <= 0 || box.height <= 0) return [radius, radius];
  const px = Math.min(CORNER_PX, box.width / 4, box.height / 4);
  return [(px * 100) / box.width, (px * 100) / box.height];
}

/** A rectangle with `radiusPx` corners as drawn in a `box` px wide and tall
 *  (plan 2026-10-11): true quarter circles however the box is stretched, the
 *  radius clamped to half the shorter side (a pill at most). A proportional
 *  rectangle passes the square it is fitted into. Radius 0, or no usable
 *  box, is the plain rectangle, path for path. */
export function roundedRectanglePath(box: { width: number; height: number }, radiusPx: number): readonly BackdropPath[] {
  const px = Math.min(radiusPx, box.width / 2, box.height / 2);
  if (!(px > 0)) return FIGURE_GEOMETRY.rectangle;
  const rx = (px * 100) / box.width;
  const ry = (px * 100) / box.height;
  return [
    {
      d:
        `M${n(rx)} 0 H${n(100 - rx)} A${n(rx)} ${n(ry)} 0 0 1 100 ${n(ry)} ` +
        `V${n(100 - ry)} A${n(rx)} ${n(ry)} 0 0 1 ${n(100 - rx)} 100 ` +
        `H${n(rx)} A${n(rx)} ${n(ry)} 0 0 1 0 ${n(100 - ry)} ` +
        `V${n(ry)} A${n(rx)} ${n(ry)} 0 0 1 ${n(rx)} 0 Z`,
      filled: true,
    },
  ];
}

/** A callout's geometry for a box in px: its body corners stay round when
 *  the bubble is stretched (see `cornerRadii`). Without a box, the table's. */
export function calloutGeometryFor(tailName: CalloutTail, box?: { width: number; height: number }): CalloutGeometry {
  return callout(tailName, box);
}

function callout(tailName: CalloutTail, box?: { width: number; height: number }): CalloutGeometry {
  const tail = TAILS[tailName];
  const body: CalloutBodyBox = {
    left: tail?.edge === "left" ? TAIL_DEPTH : 0,
    top: tail?.edge === "top" ? TAIL_DEPTH : 0,
    right: tail?.edge === "right" ? 100 - TAIL_DEPTH : 100,
    bottom: tail?.edge === "bottom" ? 100 - TAIL_DEPTH : 100,
  };
  const { left: x0, top: y0, right: x1, bottom: y1 } = body;
  const [rx, ry] = cornerRadii(CALLOUT_RADIUS, box);
  const notch = (edge: Tail["edge"]): string =>
    tail && tail.edge === edge ? tail.notch.map(([x, y]) => ` L${n(x)} ${n(y)}`).join("") : "";
  const a = `A${n(rx)} ${n(ry)} 0 0 1`;
  const d =
    `M${n(x0 + rx)} ${n(y0)}${notch("top")} L${n(x1 - rx)} ${n(y0)} ${a} ${n(x1)} ${n(y0 + ry)}` +
    `${notch("right")} L${n(x1)} ${n(y1 - ry)} ${a} ${n(x1 - rx)} ${n(y1)}` +
    `${notch("bottom")} L${n(x0 + rx)} ${n(y1)} ${a} ${n(x0)} ${n(y1 - ry)}` +
    `${notch("left")} L${n(x0)} ${n(y0 + ry)} ${a} ${n(x0 + rx)} ${n(y0)} Z`;
  return { paths: [{ d, filled: true }], body };
}

export const CALLOUT_GEOMETRY: Readonly<Record<CalloutTail, CalloutGeometry>> = {
  bottom_left: callout("bottom_left"),
  bottom_right: callout("bottom_right"),
  top_left: callout("top_left"),
  top_right: callout("top_right"),
  left: callout("left"),
  right: callout("right"),
  none: callout("none"),
};
